<template>
  <section class="lab-report" role="dialog" aria-modal="true" aria-label="Performance test report">
    <header class="lab-report-header">
      <button type="button" class="lab-report-back pointer" @click="$emit('close')">
        <span aria-hidden="true">←</span> Dashboard
      </button>
      <div class="lab-report-title">
        <h2>Performance Test Report</h2>
        <span class="sub">{{ title }}</span>
        <span v-if="report" :class="['lab-report-badge', report.summary.complete ? 'complete' : 'partial']">
          {{ report.summary.complete ? 'COMPLETE' : 'PARTIAL' }}
        </span>
      </div>
      <div class="lab-report-actions">
        <div class="lab-report-sheets" role="tablist" aria-label="Report sheets">
          <button
            v-for="sheet in availableSheets"
            :key="sheet.name"
            type="button"
            role="tab"
            :aria-selected="sheet.name === currentSheet"
            :class="['sheet-tab', 'pointer', { on: sheet.name === currentSheet }]"
            v-tooltip="sheet.description"
            @click="currentSheet = sheet.name"
          >{{ sheet.name }}</button>
        </div>
        <button type="button" class="lab-report-download pointer" :disabled="state !== 'ready'" @click="$emit('download')">
          <span aria-hidden="true">⤓</span> Download Excel
        </button>
      </div>
    </header>

    <div v-if="report && state !== 'error'" class="lab-report-note">
      <p>{{ coverageText }} {{ samplingText }} {{ readingsText }}</p>
      <div class="note-actions">
        <button v-if="newerReadings > 0" type="button" class="lab-report-update pointer" @click="$emit('rebuild')">
          {{ newerReadings.toLocaleString() }} newer {{ newerReadings === 1 ? 'reading' : 'readings' }} · Update report
        </button>
        <button v-if="scale < 1" type="button" class="lab-report-update pointer" @click="fit = !fit">
          {{ fit ? 'Actual size' : 'Fit to screen' }}
        </button>
      </div>
    </div>

    <div ref="body" class="lab-report-body">
      <div v-if="state === 'error'" class="lab-report-empty error">
        <p>{{ error }}</p>
        <button type="button" class="lab-report-update pointer" @click="$emit('rebuild')">Try again</button>
      </div>
      <div v-else-if="!table" class="lab-report-empty">Preparing the report…</div>
      <div v-else class="lab-report-frame" :style="frameStyle">
        <div ref="paper" :class="['lab-report-paper', { refreshing: state === 'building' }]" :style="paperStyle">
          <table class="xl-sheet" :style="{ width: `${table.width}px` }">
            <colgroup>
              <col v-for="(width, i) in table.columns" :key="i" :style="{ width: `${width}px` }" />
            </colgroup>
            <tbody>
              <tr v-for="row in table.rows" :key="row.key" :style="{ height: `${row.height}px` }">
                <td
                  v-for="cell in row.cells"
                  :key="cell.key"
                  :rowspan="cell.rowspan"
                  :colspan="cell.colspan"
                  :style="cell.style"
                  :data-fit="cell.fit && cell.text ? '' : null"
                >{{ cell.text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { sheetToTable } from './reportSheet'

// Text is never shrunk below this share of its size
const MIN_SHRINK = 0.6

const SHEETS = [
  { name: 'S (1)', description: 'Unit details, capacity, power and electrical readings' },
  { name: 'S (2)', description: 'Air side, psychrometrics and refrigerant side' },
  { name: 'TC', description: 'Indoor and outdoor thermocouples' },
  { name: 'Readings', description: 'Every channel at the time picked on the dashboard\'s timeline' }
]

// The lab's Excel Performance Test Report for a streamed test, shown as the
// workbook that downloads. The dashboard builds the workbook; this panel
// lays out one sheet at a time and explains what readings it covers.
export default {
  name: 'LabTestReport',
  props: {
    // Frozen { workbook, fileName, summary } from buildStreamedReport
    report: { type: Object, default: null },
    // building | ready | error
    state: { type: String, default: 'building' },
    error: { type: String, default: '' },
    title: { type: String, default: '' },
    // The stream as the dashboard sees it now: live | stalled | completed | failed
    streamState: { type: String, default: 'completed' },
    // Readings that arrived after the report was built
    newerReadings: { type: Number, default: 0 }
  },
  data () {
    return {
      currentSheet: SHEETS[0].name,
      // On a screen narrower than the page, the page is scaled to fit unless
      // the viewer asks for it at actual size (then it scrolls sideways)
      fit: true,
      scale: 1,
      paperWidth: 0,
      paperHeight: 0
    }
  },
  computed: {
    availableSheets () {
      return SHEETS.filter(s => this.report?.workbook.getWorksheet(s.name))
    },
    shownScale () {
      return this.fit ? this.scale : 1
    },
    // The frame takes the scaled page's size, as a CSS transform alone leaves
    // the page's full size in the layout
    frameStyle () {
      if (this.shownScale === 1) return null
      return { width: `${this.paperWidth * this.shownScale}px`, height: `${this.paperHeight * this.shownScale}px` }
    },
    paperStyle () {
      if (this.shownScale === 1) return null
      return { transform: `scale(${this.shownScale})`, transformOrigin: 'top left' }
    },
    readingsText () {
      const at = this.report?.summary.readingsAt
      return at ? `The Readings sheet is at ${at}; move the dashboard's timeline to change it.` : ''
    },
    table () {
      const sheet = this.report?.workbook.getWorksheet(this.currentSheet)
      return sheet ? sheetToTable(sheet) : null
    },
    coverageText () {
      const s = this.report.summary
      const readings = s.readings.toLocaleString()
      const range = `${s.start}–${s.end}`
      if (s.complete) return `Test ended at ${s.end}. Built from all ${readings} readings (${range}).`
      if (this.streamState === 'failed') return `The stream ended with an error at ${s.end}. Built from the ${readings} readings received (${range}).`
      if (this.streamState === 'live') return `Test in progress. Built from the ${readings} readings received by ${s.end}.`
      return `No readings since ${s.end}; the lab PC may have stopped sending. Built from the ${readings} received (${range}).`
    },
    samplingText () {
      const s = this.report.summary
      if (s.standardSampling) return 'Seven readings 5 min apart, ending with the last: the lab\'s standard sampling.'
      const count = s.samples === 1 ? 'One reading' : `${s.samples} readings ${s.interval} apart`
      return `${count}, ending with the last. The lab's 5 min spacing needs 35 min of readings; this run has ${formatSpan(s.spanMs)}.`
    }
  },
  watch: {
    table () {
      this.$nextTick(this.layoutPage)
    }
  },
  mounted () {
    // Escape returns to the dashboard rather than closing it; this listener
    // runs before the overlay's and stops the key there.
    window.addEventListener('keydown', this.onKey, true)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure())
      this.resizeObserver.observe(this.$refs.body)
    }
    this.$nextTick(this.layoutPage)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKey, true)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    // The template's labels are sized for Excel's shrink-to-fit, so a long
    // one ("Test Request No.") is drawn smaller rather than cut off.
    shrinkToFit () {
      const cells = this.$el.querySelectorAll('.xl-sheet td[data-fit]')
      cells.forEach(td => {
        td.style.fontSize = ''
        if (td.scrollWidth <= td.clientWidth) return
        const size = parseFloat(getComputedStyle(td).fontSize)
        const scale = Math.max(MIN_SHRINK, td.clientWidth / td.scrollWidth)
        td.style.fontSize = `${Math.floor(size * scale * 10) / 10}px`
      })
    },
    layoutPage () {
      this.shrinkToFit()
      this.measure()
    },
    // Transforms do not change offsetWidth/Height, so the page's own size is
    // read whatever scale it is shown at
    measure () {
      const body = this.$refs.body
      const paper = this.$refs.paper
      if (!body || !paper) return
      this.paperWidth = paper.offsetWidth
      this.paperHeight = paper.offsetHeight
      const style = getComputedStyle(body)
      const room = body.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      this.scale = this.paperWidth ? Math.min(1, room / this.paperWidth) : 1
    },
    onKey (e) {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      this.$emit('close')
    }
  }
}

const formatSpan = (ms) => {
  const minutes = Math.round(ms / 60000)
  if (minutes < 1) return `${Math.max(1, Math.round(ms / 1000))} s`
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}
</script>

<style scoped lang="scss">
// Covers the whole dashboard overlay, whatever the dashboard is scrolled to
.lab-report {
  position: fixed;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  background: #e4e7ec;
  color: var(--lab-text);
}

.lab-report-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 20px;
  background: var(--lab-header-bg);
  color: var(--lab-header-text);
}

.lab-report-back {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-header-text);
  background: transparent;
  border: 1px solid #475569;
  border-radius: 4px;
  white-space: nowrap;

  &:hover { background: #334155; border-color: #64748b; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
}

.lab-report-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex: 1;

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  .sub {
    font-size: 12px;
    color: #9ca3af;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.lab-report-badge {
  align-self: center;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;

  &.complete { background: #166534; color: #dcfce7; }
  &.partial { background: #b45309; color: #fff; }
}

.lab-report-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lab-report-sheets {
  display: flex;
  border: 1px solid #475569;
  border-radius: 4px;
  overflow: hidden;
}

.sheet-tab {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
  background: transparent;
  border: none;
  border-right: 1px solid #475569;
  white-space: nowrap;

  &:last-child { border-right: none; }
  &:hover { background: #334155; }
  &.on { background: #e8eaf0; color: var(--lab-header-bg); }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: -2px; }
}

.lab-report-download {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #2563eb;
  border: 1px solid #2563eb;
  border-radius: 4px;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #1d4ed8; }
  &:disabled { opacity: 0.5; cursor: default; }
  &:focus-visible { outline: 2px solid #93c5fd; outline-offset: 2px; }
}

.lab-report-note {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px 16px;
  padding: 8px 20px;
  background: var(--lab-surface);
  border-bottom: 1px solid var(--lab-border);
  font-size: 12px;
  color: var(--lab-muted);

  p { margin: 0; flex: 1; line-height: 1.45; }
}

.note-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.lab-report-frame {
  margin: 0 auto;
}

.lab-report-update {
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-accent);
  background: var(--lab-surface);
  border: 1px solid var(--lab-accent);
  border-radius: 4px;
  white-space: nowrap;

  &:hover { background: #eef2f7; }
}

.lab-report-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 24px;
}

.lab-report-paper {
  width: max-content;
  margin: 0 auto;
  padding: 28px 32px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12), 0 8px 24px rgba(15, 23, 42, 0.08);
  transition: opacity 0.2s;

  &.refreshing { opacity: 0.6; }
}

// The template's own look: Arial at 9 pt, no gridlines, borders as drawn
.xl-sheet {
  table-layout: fixed;
  border-collapse: collapse;
  font-family: 'Arial Unicode MS', Arial, Helvetica, sans-serif;
  font-size: 12px;
  color: #000;
  font-variant-numeric: tabular-nums;

  td {
    padding: 0 3px;
    overflow: hidden;
    line-height: 1.2;
  }
}

.lab-report-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  color: var(--lab-muted);
  font-size: 14px;

  p { margin: 0; }
  &.error { color: #b91c1c; }
}

// Phones: title on one row, sheets and download on the next; the page is
// scaled to the screen's width by `measure`
@media (max-width: 640px) {
  .lab-report-header {
    gap: 8px 10px;
    padding: 8px 12px;
  }

  .lab-report-title {
    flex-basis: calc(100% - 110px);
    flex-wrap: wrap;
    row-gap: 2px;

    h2 { font-size: 14px; }
  }

  .lab-report-actions {
    flex-basis: 100%;
    justify-content: space-between;
  }

  .sheet-tab,
  .lab-report-back { padding: 7px 10px; }

  .lab-report-download { padding: 7px 12px; }

  .lab-report-note {
    flex-wrap: wrap;
    padding: 8px 12px;
  }

  .lab-report-body { padding: 10px; }

  .lab-report-paper { padding: 16px; }
}

@media (prefers-reduced-motion: reduce) {
  .lab-report-paper { transition: none; }
}
</style>
